import { useEffect, useRef, useState } from "react";
import {
  getHandle,
  hasHandle,
  HandleModal,
  Leaderboard,
  ShareSheet,
  track,
  trackPageView,
  useCrash,
  useLeaderboard,
} from "@karrtopelka/game-kit";
import { fromScoreValue } from "@karrtopelka/game-api/crash";
import type { CardProps } from "@karrtopelka/cards/types";

import { Climb } from "./components/Climb.js";
import { buildCrashCard } from "./lib/buildCard.js";
import { loadCardAssets } from "./lib/cardAssets.js";
import {
  heatClass,
  loadHistory,
  pushHistory,
  type PastRound,
} from "./lib/history.js";
import { useCrashStore } from "./store.js";

/** One shared leaderboard instance: desktop rail + mobile post-round board. */
type Board = ReturnType<typeof useLeaderboard>;

const SIBLINGS = [
  { name: "wheel", hook: "push your luck" },
  { name: "limbo", hook: "call your shot" },
  { name: "plinko", hook: "one ball. 470x." },
] as const;

export function App() {
  const phase = useCrashStore((s) => s.phase);
  const result = useCrashStore((s) => s.result);
  const best = useCrashStore((s) => s.best);

  // Funnel beacons: one page_view per load, one play_start per session, one
  // play_done per resolved round (cashed or crashed).
  useEffect(() => trackPageView("crash"), []);
  const started = useRef(false);
  useEffect(() => {
    if (phase === "climbing" && !started.current) {
      started.current = true;
      track("crash", "play_start");
    }
    if (phase === "cashed" || phase === "crashed") track("crash", "play_done");
  }, [phase]);

  // Last-rounds strip: append once per resolved round.
  const [history, setHistory] = useState<PastRound[]>(() => loadHistory());
  const pushedFor = useRef<unknown>(null);
  useEffect(() => {
    if ((phase !== "cashed" && phase !== "crashed") || !result) return;
    if (pushedFor.current === result) return;
    pushedFor.current = result;
    if (result.crashPoint === undefined) return;
    setHistory(
      pushHistory({
        cp: result.crashPoint,
        cashed: result.won ? (result.multiplier ?? null) : null,
      }),
    );
  }, [phase, result]);

  // Leaderboard lifted to App level so the desktop rail stays live during play;
  // refresh once per resolved round so a fresh cashout shows up immediately.
  const board = useLeaderboard("crash");
  const { refresh } = board;
  useEffect(() => {
    if (phase === "cashed" || phase === "crashed") void refresh();
  }, [phase, refresh]);

  const myHandle = getHandle();
  const cashedCount = history.filter((r) => r.cashed !== null).length;

  return (
    <main
      className="flex min-h-screen min-h-[100dvh] flex-col items-center gap-5 bg-bg px-6 text-text lg:grid lg:grid-cols-[minmax(200px,260px)_minmax(0,44rem)_minmax(240px,300px)] lg:content-start lg:items-start lg:justify-center lg:gap-10 lg:px-10"
      style={{
        paddingTop: "calc(2.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(2.5rem + env(safe-area-inset-bottom))",
      }}
    >
      <header className="flex animate-fade-up flex-col items-center lg:col-span-3 lg:justify-self-center">
        <h1 className="font-display text-3xl font-extrabold tracking-tight lg:text-5xl">
          crash
          <span className="text-accent lg:drop-shadow-[0_0_14px_rgba(255,111,165,0.7)]">
            .
          </span>
        </h1>
        <p className="text-sm text-text-muted lg:mt-1">
          cash out before it crashes.
        </p>
      </header>

      {/* LEFT rail — desktop only: rules, your numbers, keys. Quiet furniture. */}
      <aside
        className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-4"
        style={{ animationDelay: "120ms" }}
      >
        <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            how it works
          </h2>
          <ul className="mt-2 space-y-1.5 text-sm text-text-faint">
            <li>the line climbs, so does the multiplier.</li>
            <li>cash out any time — that&apos;s your score.</li>
            <li>hold one beat too long, it&apos;s all gone.</li>
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-surface/60 p-4 backdrop-blur transition-all duration-200 hover:border-accent">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            your best
          </h2>
          <span className="mt-1 block font-display text-3xl font-extrabold text-accent">
            {best > 0 ? `${fromScoreValue(best)}x` : "—"}
          </span>
          {history.length > 0 && (
            <p className="mt-1 text-xs text-text-faint">
              cashed {cashedCount} of your last {history.length}
            </p>
          )}
        </div>

        <div className="rounded-lg border border-border bg-surface/60 p-4 text-sm text-text-faint backdrop-blur">
          <kbd className="rounded border border-border bg-surface-raised px-1.5 py-0.5 font-mono text-xs text-text">
            space
          </kbd>{" "}
          cashes out mid-climb
        </div>
      </aside>

      {/* CENTER — the game. */}
      <div className="flex w-full flex-col items-center gap-5 lg:min-w-0 lg:max-w-2xl lg:justify-self-center">
        {/* Your last rounds — the pattern-seeking itch. Hot rounds glow. */}
        {history.length > 0 && (
          <div
            className="flex max-w-md animate-fade-up flex-wrap items-center justify-center gap-1.5 lg:max-w-2xl"
            style={{ animationDelay: "80ms" }}
          >
            {history.map((r, i) => (
              <span
                key={i}
                className={`rounded-full border px-2 py-0.5 font-mono text-[11px] ${heatClass(r.cp)} ${
                  i === 0 ? "animate-pop" : ""
                }`}
                title={
                  r.cashed !== null
                    ? `cashed ${r.cashed.toFixed(2)}x`
                    : "rode it down"
                }
              >
                {r.cashed !== null
                  ? `✓${r.cashed.toFixed(2)}x`
                  : `${r.cp.toFixed(2)}x`}
              </span>
            ))}
          </div>
        )}

        {phase === "idle" && <Idle />}
        {phase === "climbing" && <Climbing />}
        {(phase === "cashed" || phase === "crashed") && <Result board={board} />}
      </div>

      {/* RIGHT rail — desktop only: the wall of winners, live during play. */}
      <aside
        className="hidden animate-fade-up lg:sticky lg:top-8 lg:flex lg:flex-col lg:gap-3"
        style={{ animationDelay: "200ms" }}
      >
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface/60 p-4 backdrop-blur">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            best crash
          </h2>
          <Leaderboard
            entries={board.entries}
            loading={board.loading}
            myHandle={myHandle}
            formatValue={(v) => `${fromScoreValue(v)}x`}
            emptyLabel="no one's cashed out yet — go."
            range={board.range}
            onRange={board.setRange}
          />
        </div>
      </aside>

      <footer
        className="mt-2 flex animate-fade-up flex-col items-center gap-1 text-xs text-text-faint lg:col-span-3 lg:mt-6 lg:justify-self-center"
        style={{ animationDelay: "240ms" }}
      >
        {/* mobile: tiny cross-links, as ever */}
        <div className="flex flex-col items-center gap-1 lg:hidden">
          <a
            href="https://wheel.karrtopelka.com"
            className="hover:text-accent hover:underline"
          >
            also try wheel — push your luck →
          </a>
          <a href="https://karrtopelka.com" className="hover:text-text-muted">
            karrtopelka.com
          </a>
        </div>

        {/* desktop: more games strip */}
        <div className="hidden items-center gap-3 lg:flex">
          {SIBLINGS.map((g) => (
            <a
              key={g.name}
              href={`https://${g.name}.karrtopelka.com`}
              className="group flex flex-col items-center rounded-lg border border-border bg-surface/60 px-5 py-2.5 backdrop-blur transition-all duration-200 hover:-translate-y-0.5 hover:border-accent hover:shadow-glow"
            >
              <span className="font-display text-sm font-bold text-text transition-colors duration-200 group-hover:text-accent">
                {g.name}.
              </span>
              <span className="text-[11px] text-text-faint">{g.hook}</span>
            </a>
          ))}
          <a
            href="https://karrtopelka.com"
            className="ml-2 transition-colors duration-200 hover:text-accent"
          >
            all games →
          </a>
        </div>
      </footer>
    </main>
  );
}

function Idle() {
  const best = useCrashStore((s) => s.best);
  const begin = useCrashStore((s) => s.begin);
  const { start } = useCrash("crash");
  const [starting, setStarting] = useState(false);
  const [showHandle, setShowHandle] = useState(false);

  const launch = async () => {
    setStarting(true);
    const round = await start();
    setStarting(false);
    if (round) begin(round);
  };

  // Collect a handle BEFORE the round so cash out never has to interrupt the climb.
  const onStart = () => {
    if (hasHandle()) void launch();
    else setShowHandle(true);
  };

  return (
    <div
      className="flex w-full max-w-md animate-fade-up flex-col items-center gap-4 lg:max-w-2xl"
      style={{ animationDelay: "160ms" }}
    >
      <div className="relative flex aspect-square w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border border-border bg-surface lg:aspect-[16/10]">
        {/* ghost of the climb to come */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full animate-float text-accent opacity-20"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden
        >
          <path
            d="M 0 96 C 45 91, 76 60, 100 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
            strokeDasharray="5 7"
            strokeLinecap="round"
          />
        </svg>
        {/* hairline scanlines — lit-cabinet texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03] [background:repeating-linear-gradient(0deg,#ffffff_0_1px,transparent_1px_3px)]"
          aria-hidden
        />
        <span className="font-display text-6xl font-extrabold text-text-faint lg:text-7xl">
          1.00x
        </span>
        {best > 0 && (
          <span className="text-sm text-text-faint">
            your best: {fromScoreValue(best)}x
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onStart}
        disabled={starting}
        className="animate-neon-pulse w-full rounded-md bg-accent px-4 py-4 text-lg font-bold text-bg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-glow active:scale-[0.99] disabled:animate-none disabled:opacity-40"
      >
        {starting ? "starting…" : "start round"}
      </button>

      {showHandle && (
        <HandleModal
          onCancel={() => setShowHandle(false)}
          onDone={() => {
            setShowHandle(false);
            void launch();
          }}
        />
      )}
    </div>
  );
}

function Climbing() {
  const round = useCrashStore((s) => s.round);
  const lockCashout = useCrashStore((s) => s.lockCashout);
  const setCashoutResult = useCrashStore((s) => s.setCashoutResult);
  const revealAtCrash = useCrashStore((s) => s.revealAtCrash);
  const { cashout } = useCrash("crash");
  const cashedOnce = useRef(false);

  if (!round) return null;

  // Player tapped cash out — lock the displayed number + resolve server-side. The result
  // is stashed in the store; the climb keeps running. The store reconciles the race
  // (whichever of cashout-resolved / crash-reached happens last triggers the reveal).
  const onCashout = async (multiplier: number) => {
    if (cashedOnce.current) return;
    cashedOnce.current = true;
    lockCashout(multiplier);
    const res = await cashout(round, multiplier);
    setCashoutResult(
      res ?? { won: true, multiplier, crashPoint: round.crashPoint },
    );
  };

  return (
    <Climb
      crashPoint={round.crashPoint}
      onCashout={onCashout}
      onCrashReached={() => revealAtCrash(round.crashPoint)}
    />
  );
}

function Result({ board }: { board: Board }) {
  const phase = useCrashStore((s) => s.phase);
  const result = useCrashStore((s) => s.result);
  const best = useCrashStore((s) => s.best);
  const reset = useCrashStore((s) => s.reset);

  const { entries, loading, range, setRange } = board;
  const [handle] = useState<string | null>(() => getHandle());
  const [card] = useState<CardProps | null>(() =>
    result ? buildCrashCard(getHandle() ?? "you", result) : null,
  );

  const won = phase === "cashed" && result?.won === true;
  const isBest = won && result?.value !== undefined && result.value >= best;

  return (
    <div className="flex w-full max-w-md animate-fade-up flex-col items-center gap-6">
      <section className="flex flex-col items-center">
        {won ? (
          <>
            {isBest && (
              <span className="mb-2 animate-pop rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-bg">
                ⚡ new best
              </span>
            )}
            <span
              className={`font-display text-7xl font-extrabold lg:text-8xl ${
                isBest ? "animate-glow text-accent" : "text-text"
              }`}
            >
              {result?.multiplier?.toFixed(2)}x
            </span>
            <span className="mt-1 text-sm text-text-muted">cashed out</span>
            {result?.crashPoint !== undefined &&
              result.multiplier !== undefined && (
                <span className="mt-1 text-xs text-text-faint">
                  it crashed at {result.crashPoint.toFixed(2)}x · left{" "}
                  {(result.crashPoint - result.multiplier).toFixed(2)}x on the table
                </span>
              )}
            {result?.total !== undefined && result.total >= 10 && (
              <p className="mt-2 text-sm text-text">
                #{result.rank} of {result.total}
                <span className="text-accent"> · top {result.percentile}%</span>
              </p>
            )}
          </>
        ) : (
          <>
            <span className="mb-2 animate-shake text-lg font-bold uppercase tracking-wide text-danger">
              💥 crashed
            </span>
            <span className="font-display text-6xl font-extrabold text-text-faint lg:text-7xl">
              {result?.crashPoint?.toFixed(2)}x
            </span>
            <span className="mt-1 text-sm text-text-muted">
              it crashed — you held too long
            </span>
          </>
        )}
      </section>

      {card && (
        <ShareSheet
          card={card}
          replyLink="crash.karrtopelka.com/r"
          caption={
            won
              ? `cashed out at ${result?.multiplier?.toFixed(2)}x. ${card.provocation}`
              : `crashed at ${result?.crashPoint?.toFixed(2)}x. ${card.provocation}`
          }
          loadAssets={loadCardAssets}
        />
      )}

      <button
        type="button"
        onClick={reset}
        className="rounded-md border border-transparent px-3 py-1.5 text-sm text-text-muted transition-all duration-200 hover:-translate-y-0.5 hover:border-border hover:text-text"
      >
        play again
      </button>

      {/* mobile keeps the post-round board; desktop has the persistent rail */}
      <section className="flex w-full flex-col items-center gap-3 border-t border-border pt-6 lg:hidden">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          highest cashouts
        </h2>
        <Leaderboard
          entries={entries}
          loading={loading}
          myHandle={handle}
          formatValue={(v) => `${fromScoreValue(v)}x`}
          emptyLabel="no one's cashed out yet — go."
          range={range}
          onRange={setRange}
        />
      </section>
    </div>
  );
}
